import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useActions } from 'hooks/useActions';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { ReactComponent as DownloadSvg } from 'assets/svg/upload.svg';
import { ReactComponent as SearchSvg } from 'assets/svg/search.svg';
import { ReactComponent as ThreeDotsSvg } from 'assets/svg/threedots.svg';
import { ReactComponent as UserSvg } from 'assets/svg/user.svg';
import { ReactComponent as LogoutSvg } from 'assets/svg/logout.svg';
import { ReactComponent as DownArrowSvg } from 'assets/svg/arrowDown.svg';
import Divider from 'components/UI/Divider';
import Table from 'components/UI/Table';
import Calendar from 'components/UI/Calendar';
import PaginationComponent from 'components/UI/Pagination';
import PageSizer from 'components/UI/PageSizer/PageSizer';
import { digestHeader, digestTemplateHeader, tableHeader } from 'helpers';
import NoAvatarSvg from 'assets/svg/noAvatar.svg';
import tableContainer from './TableContainer';
import CsvButton from 'components/CsvButton/CsvButton';
import styles from './Digests.module.scss';
import { digests } from 'store/reducers/Digests';
import {ReactComponent as UploadSvg} from 'assets/svg/upload.svg';
import DigestTemplateForm, { CreateTemplate, FormRef } from './DigestTemplateForm';
import { message, Modal } from 'antd';
import { ICreateDigestTemplateParams } from 'api/digests';
import { logout } from 'store/action-creators/Auth';
import { DeleteOutlined } from '@ant-design/icons';

const DigestTemplates = () => {
    const { createTemplate, getTemplates, uploadBulkTemplates, addRSSFile, deleteDigestTemplates } = useActions();
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
    
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const formRef = useRef<FormRef>(null);
    const [csv, setCSV] = useState<File | null>(null);
    const [formData, setFormData] = useState<CreateTemplate | undefined>(undefined)

    const { templates, dist: templateDist } = useTypedSelector((state) => state.digestTemplates);

    const filesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files && event.target.files[0];
        if (file && file.type === "text/csv") {
            setCSV(file)
        } else {
            console.error('Please select a CSV file.');
        }
    };

    const submit = async () => {
        if(csv){
            const url = await addRSSFile(csv)
            if(fileInputRef && fileInputRef.current && fileInputRef.current.value){
                fileInputRef.current.value = ''; 
            }

            const rssUrl = url as any as string

            if(rssUrl){
                const upload = await uploadBulkTemplates(rssUrl)
                message.info("Templates Uploaded")
            }
        }else{
            message.error("Please select a file")
        }
    }

    const handleDelete = async (templateId: string) => {
        setTemplateToDelete(templateId);
        setDeleteModalVisible(true);
    };

    const handleDeleteConfirm = async () => {
        if (!templateToDelete) {
            setDeleteModalVisible(false);
            setTemplateToDelete(null);
            return
        }

        try {
            await deleteDigestTemplates(templateToDelete);
            getTemplates({
                searchParams: templatesSearchParams,
                sort: templatesSort,
                page: templatesPage,
                dateFrom: templatesStartDate,
                dateTo: templatesEndDate,
                limit: templatesLimit
            });
        } catch (error) {
            message.error('Failed to delete template');
        } finally {
            setDeleteModalVisible(false);
            setTemplateToDelete(null);
            message.success('Template deleted successfully');
        }
    };

    const navigate = useNavigate();
    useEffect(() => {
        navigate('/templates?sort=datedesc');
    }, []);

    const {
        onChangeHandler: onTemplatesChangeHandler,
        endDate: templatesEndDate,
        selectHandler: templatesSelectHandler,
        defaultPageSize: templatesdefauletPageSize,
        onShowSizeChange: templatesOnShowSizeChange,
        onChangePagination: templatesOnChangePagination,
        searchParams: templatesSearchParams,
        searchDigestsHandler: templatesSearchDigestsHandler,
        sort: templatesSort,
        dd: templatesdd,
        page: templatesPage,
        limit: templatesLimit,
        startDate: templatesStartDate,
    } = tableContainer({ getData: getTemplates, locationParams: 'templates' });

    const handleCancel = () => {
        setIsModalOpen(false);
      };

      const handleOk = async () => {
        setConfirmLoading(true);
        try {
            const values = await formRef.current?.submit();

            if (values) {
                await createTemplate(values)
                message.info("Template Created")
                formRef.current?.clear();
                setFormData(undefined)
                setIsModalOpen(false);
                getTemplates({
                    searchParams: templatesSearchParams,
                    sort: templatesSort,
                    page: templatesPage,
                    dateFrom: templatesStartDate,
                    dateTo: templatesEndDate,
                    limit: templatesLimit
                });
            }
        } catch (error: any) {
            error.message && message.error(error.message)
        } finally {
            setConfirmLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.wrapper}>
                <h1 className={styles.mainTitle}>Digests Templates</h1>
                <p className={styles.mobileTitleSubtext}>View all digests templates</p>
                <div className={styles.buttonsContainer}>
                    <div className={styles.downloadBtn}>
                        <CsvButton data={templates}>
                            {' '}
                            <DownloadSvg /> Download CSV
                        </CsvButton>
                    </div>
                </div>
            </div>
            <div className={styles.navigationBox}>
                <nav className={styles.navContainer}>
                    <ul>
                        <li>All Templates</li>
                    </ul>
                </nav>
                <div className={styles.logoutContainer} onClick={logout}>
                    <LogoutSvg className={styles.logoutSvg} />
                    <span className={styles.text}>Log Out</span>
                </div>
            </div>

            <div className={styles.divider}>
                <Divider />
            </div>

            <div className={styles.tableHeader}>
                <h2 className={styles.title}>All Digest Templates - Total {templateDist}</h2>
                <div >
                    <input
                        name={"Upload CSV"}
                        className={styles.fileInput}
                        type='file'
                        ref={fileInputRef}
                        accept='.csv'
                        onChange={filesSelected}
                    />
                    <button type="button" className={styles.addBtn} onClick={submit}>
                        <UploadSvg></UploadSvg>
                    </button>
                </div>
                <button 
                    className={styles.addButton}
                    onClick={() => setIsModalOpen(true)}
                >
                    Add Template
                </button>

                <Modal
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    okText="Submit"
                    onOk={handleOk}
                    title="Add Digest Template"
                    confirmLoading={confirmLoading}
                    onCancel={handleCancel}
                >
                    <DigestTemplateForm 
                        ref={formRef}
                        onClose={() => setIsModalOpen(false)}
                        setFormData={setFormData}
                    />
                </Modal>
            </div>

            <Table>
                <thead>
                    <tr>
                        {digestTemplateHeader.map((item) => {
                            return (
                                <th key={item.id}>
                                        <div className={styles.columnHeader}>
                                        {item.title}
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {templates.map((template) => {
                        return (
                            <tr key={template.id}>
                                 <td className={styles.digestNumberColumn}>
                                    <span className={styles.name}>{template.id}</span>
                                </td>
                                <td className={styles.digestNumberColumn}>{template.name}</td>
                                 <td className={styles.digestNumberColumn}>{template.description}</td>
                                 <td className={styles.digestNumberColumn}>{template.userEmail}</td>
                                 <td className={styles.digestNumberColumn}>{template.category}</td>
                                 <td className={styles.digestNumberColumn}>{template.users_count}</td>
                                 <td className={styles.dateColumn}>{dayjs(template.createdAt).format('MMM DD YYYY')}</td>
                                 <td className={styles.actionColumn}>
                                <button
                                    className={styles.deleteButton}
                                    onClick={() => handleDelete(template.id)}
                                >
                                    <DeleteOutlined />
                                </button>
                            </td>
                            </tr>
                        );
                    })}
                </tbody>
                <Modal
                    title="Delete Template"
                    open={deleteModalVisible}
                    onOk={handleDeleteConfirm}
                    onCancel={() => {
                        setDeleteModalVisible(false);
                        setTemplateToDelete(null);
                    }}
                    okText="Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                >
                    <p>Are you sure you want to delete this template?</p>
                </Modal>
            </Table>
            <div className={styles.tableFooter}>
                <div className={styles.pageSizerContainer}>
                    <UserSvg />
                    <span className={styles.text}> Templates per page</span>
                    <div className={styles.sizerWrapper}>
                        <PageSizer currentItem={templatesLimit || '10'} onClick={templatesSelectHandler} array={['10', '20', '50']} />
                    </div>
                </div>
                <PaginationComponent
                    defaultPageSize={templatesdefauletPageSize}
                    total={templateDist}
                    defaultCurrent={templatesPage ? Number(templatesPage) : 1}
                    onChange={templatesOnChangePagination}
                    onShowSizeChange={templatesOnShowSizeChange}
                />
            </div>

        </div>
    );
};

export default DigestTemplates;
